import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { kuendigungenApi } from '../api';

// ─── Konstanten ────────────────────────────────────────────────────────────────

const STATUS_LABELS = {
  entwurf: 'Entwurf',
  vorbereitet: 'Vorbereitet',
  freigegeben: 'Freigegeben',
  versendet: 'Versendet',
  antwort_erhalten: 'Antwort erhalten',
  bestaetigt: 'Bestätigt',
  abgeschlossen: 'Abgeschlossen',
  archiviert: 'Archiviert',
  zurueckgezogen: 'Zurückgezogen',
};

const STATUS_FARBE = {
  entwurf: '#94a3b8',
  vorbereitet: '#38bdf8',
  freigegeben: '#34d399',
  versendet: '#fbbf24',
  antwort_erhalten: '#fb923c',
  bestaetigt: '#a78bfa',
  abgeschlossen: '#34d399',
  archiviert: '#475569',
  zurueckgezogen: '#f87171',
};

// Erlaubte Folgestatus je Status
const ERLAUBTE_FOLGERSTATUS = {
  entwurf: ['vorbereitet', 'zurueckgezogen', 'archiviert'],
  vorbereitet: ['freigegeben', 'entwurf', 'zurueckgezogen', 'archiviert'],
  freigegeben: ['versendet', 'vorbereitet', 'zurueckgezogen', 'archiviert'],
  versendet: ['antwort_erhalten', 'bestaetigt', 'zurueckgezogen', 'archiviert'],
  antwort_erhalten: ['bestaetigt', 'zurueckgezogen', 'archiviert'],
  bestaetigt: ['abgeschlossen', 'archiviert'],
  abgeschlossen: ['archiviert'],
  archiviert: [],
  zurueckgezogen: ['archiviert'],
};

const KOMMUNIKATION_STATUS_LABELS = {
  offen: 'Offen',
  in_bearbeitung: 'In Bearbeitung',
  erledigt: 'Erledigt',
  abgelehnt: 'Abgelehnt',
};

const NACHWEIS_TYPEN = [
  { value: 'versandnachweis', label: 'Versandnachweis' },
  { value: 'briefkasten', label: 'Briefkasten' },
  { value: 'email', label: 'E-Mail' },
  { value: 'sonstiges', label: 'Sonstiges' },
];

// ─── Hilfsfunktionen ───────────────────────────────────────────────────────────

function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function formatDate(v) {
  if (!v) return '–';
  return new Date(String(v).replace(' ', 'T')).toLocaleDateString('de-DE');
}

function formatDateTime(v) {
  if (!v) return '–';
  return new Date(String(v).replace(' ', 'T')).toLocaleString('de-DE');
}

function StatusBadge({ status }) {
  const label = STATUS_LABELS[status] || status || '–';
  const farbe = STATUS_FARBE[status] || '#94a3b8';
  return (
    <span style={{ ...badge, color: farbe, borderColor: `${farbe}66`, background: `${farbe}14` }}>
      {label}
    </span>
  );
}

function formatGroesse(bytes) {
  if (!bytes) return '–';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

// ─── Hauptkomponente ───────────────────────────────────────────────────────────

export default function KuendigungsDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [vorgang, setVorgang] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('schreiben');
  const [meldung, setMeldung] = useState('');
  const [meldungTyp, setMeldungTyp] = useState('info'); // 'info' | 'error'

  const zeigeMeldung = useCallback((text, typ = 'info') => {
    setMeldung(text);
    setMeldungTyp(typ);
    setTimeout(() => setMeldung(''), 4000);
  }, []);

  const ladeVorgang = useCallback(async () => {
    try {
      const res = await kuendigungenApi.vorgaenge.get(id);
      setVorgang(res.data.vorgang);
    } catch {
      setError('Kündigungsvorgang konnte nicht geladen werden.');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { ladeVorgang(); }, [ladeVorgang]);

  const onStatusChange = async (neuerStatus) => {
    if (!window.confirm(`Status wirklich auf "${STATUS_LABELS[neuerStatus]}" setzen?`)) return;
    try {
      const res = await kuendigungenApi.vorgaenge.setStatus(id, neuerStatus);
      setVorgang(res.data.vorgang);
      zeigeMeldung(`Status geändert: ${STATUS_LABELS[neuerStatus]}`);
    } catch (e) {
      zeigeMeldung(e.response?.data?.error || 'Fehler beim Statuswechsel.', 'error');
    }
  };

  if (loading) return <Screen text="Vorgang wird geladen..." />;
  if (error || !vorgang) return <Screen title="Fehler" text={error || 'Vorgang nicht gefunden'} />;

  const TABS = [
    { id: 'schreiben', label: 'Schreiben' },
    { id: 'nachweise', label: 'Nachweise' },
    { id: 'kommunikation', label: 'Kommunikation' },
    { id: 'details', label: 'Details' },
  ];

  return (
    <div style={page}>
      {/* Header */}
      <div style={header}>
        <div>
          <div style={eyebrow}>Kündigungsvorgang</div>
          <h1 style={titleStyle}>{vorgang.vertrag_titel || `Vertrag #${vorgang.vertrag_id}`}</h1>
          <div style={subtitleRow}>
            {vorgang.vertrag_anbieter && (
              <span style={typChip}>{vorgang.vertrag_anbieter}</span>
            )}
            <StatusBadge status={vorgang.status} />
            {vorgang.fristgerecht === 0 && (
              <span style={{ ...typChip, color: '#f87171', borderColor: '#f8717166', background: '#f8717114' }}>
                Nicht fristgerecht
              </span>
            )}
            {vorgang.sonderkuendigung === 1 && (
              <span style={{ ...typChip, color: '#fbbf24', borderColor: '#fbbf2466', background: '#fbbf2414' }}>
                Sonderkündigung
              </span>
            )}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <button className="btn-secondary" onClick={() => navigate('/kuendigungen')}>Zurück</button>
          {vorgang.vertrag_id && (
            <button className="btn-secondary" onClick={() => navigate(`/vertraege/${vorgang.vertrag_id}`)}>
              Zum Vertrag
            </button>
          )}
        </div>
      </div>

      {/* KI-Hinweis */}
      {vorgang.ki_hinweis && (
        <div style={kiHinweisBox}>
          <strong>KI-Hinweis:</strong> {vorgang.ki_hinweis}
        </div>
      )}

      {/* Sonderkündigung-Hinweis */}
      {vorgang.sonderkuendigung === 1 && vorgang.sonderkuendigungsgrund && (
        <div style={sonderHinweisBox}>
          <strong>Sonderkündigungsgrund:</strong> {vorgang.sonderkuendigungsgrund}
          <div style={{ fontSize: 12, marginTop: 4, color: '#fbbf24' }}>
            Hinweis: Die Sonderkündigung muss manuell durch Sie ausgelöst und versendet werden.
          </div>
        </div>
      )}

      {/* Meldung */}
      {meldung && (
        <div style={meldungTyp === 'error' ? errorNotice : notice}>{meldung}</div>
      )}

      {/* Tabs */}
      <div style={tabRow}>
        {TABS.map(t => (
          <button
            key={t.id}
            style={activeTab === t.id ? activeTabBtn : tabBtn}
            onClick={() => setActiveTab(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Tab-Inhalte */}
      {activeTab === 'schreiben' && (
        <SchreibenTab
          vorgangId={id}
          vorgangStatus={vorgang.status}
          vorgangTitel={vorgang.vertrag_titel}
          zeigeMeldung={zeigeMeldung}
        />
      )}
      {activeTab === 'nachweise' && (
        <NachweiseTab vorgangId={id} zeigeMeldung={zeigeMeldung} />
      )}
      {activeTab === 'kommunikation' && (
        <KommunikationTab vorgangId={id} zeigeMeldung={zeigeMeldung} />
      )}
      {activeTab === 'details' && (
        <DetailsTab
          vorgang={vorgang}
          onStatusChange={onStatusChange}
        />
      )}
    </div>
  );
}

// ─── Tab: Schreiben ────────────────────────────────────────────────────────────

function SchreibenTab({ vorgangId, vorgangStatus, vorgangTitel, zeigeMeldung }) {
  const [schreibenListe, setSchreibenListe] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editId, setEditId] = useState(null);
  const [editBetreff, setEditBetreff] = useState('');
  const [editText, setEditText] = useState('');
  const [saving, setSaving] = useState(false);
  const [exportLoading, setExportLoading] = useState('');

  const ladeSchreiben = useCallback(async () => {
    setLoading(true);
    try {
      const res = await kuendigungenApi.vorgaenge.getSchreiben(vorgangId);
      setSchreibenListe(res.data.schreiben || []);
    } catch {
      zeigeMeldung('Schreiben konnten nicht geladen werden.', 'error');
    } finally {
      setLoading(false);
    }
  }, [vorgangId, zeigeMeldung]);

  useEffect(() => { ladeSchreiben(); }, [ladeSchreiben]);

  const freigegebenesSchreiben = schreibenListe.find(s => s.freigegeben === 1 || s.freigegeben === true);

  const startEdit = (s) => {
    setEditId(s.id);
    setEditBetreff(s.betreff || '');
    setEditText(s.text || '');
  };

  const abbrechenEdit = () => {
    setEditId(null);
    setEditBetreff('');
    setEditText('');
  };

  const speichern = async (sid) => {
    setSaving(true);
    try {
      await kuendigungenApi.schreiben.update(sid, editBetreff, editText);
      zeigeMeldung('Schreiben gespeichert.');
      setEditId(null);
      ladeSchreiben();
    } catch (e) {
      zeigeMeldung(e.response?.data?.error || 'Fehler beim Speichern.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const freigeben = async (sid) => {
    if (!window.confirm('Schreiben jetzt freigeben? Nach der Freigabe kann es exportiert werden.')) return;
    try {
      await kuendigungenApi.schreiben.freigeben(sid);
      zeigeMeldung('Schreiben freigegeben.');
      ladeSchreiben();
    } catch (e) {
      zeigeMeldung(e.response?.data?.error || 'Fehler bei der Freigabe.', 'error');
    }
  };

  const regenerieren = async (sid) => {
    if (!window.confirm('Schreiben neu generieren? Der aktuelle Inhalt wird überschrieben.')) return;
    try {
      await kuendigungenApi.schreiben.regenerieren(sid, null, null);
      zeigeMeldung('Schreiben neu generiert.');
      ladeSchreiben();
    } catch (e) {
      zeigeMeldung(e.response?.data?.error || 'Fehler beim Regenerieren.', 'error');
    }
  };

  const exportieren = async (format) => {
    if (!freigegebenesSchreiben) {
      zeigeMeldung('Das Schreiben muss zuerst freigegeben werden, bevor es exportiert werden kann.', 'error');
      return;
    }
    setExportLoading(format);
    try {
      let res;
      let ext;
      let mime;
      if (format === 'pdf') {
        res = await kuendigungenApi.vorgaenge.exportPdf(vorgangId);
        ext = 'pdf';
        mime = 'application/pdf';
      } else if (format === 'docx') {
        res = await kuendigungenApi.vorgaenge.exportDocx(vorgangId);
        ext = 'docx';
        mime = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
      } else {
        res = await kuendigungenApi.vorgaenge.exportEml(vorgangId);
        ext = 'eml';
        mime = 'message/rfc822';
      }
      const blob = new Blob([res.data], { type: mime });
      const dateiname = `kuendigung_${vorgangId}_${vorgangTitel ? vorgangTitel.replace(/[^a-z0-9]/gi, '_') : 'schreiben'}.${ext}`;
      downloadBlob(blob, dateiname);
      zeigeMeldung(`Export als ${ext.toUpperCase()} erfolgreich.`);
    } catch (e) {
      zeigeMeldung(e.response?.data?.error || `Fehler beim ${format.toUpperCase()}-Export.`, 'error');
    } finally {
      setExportLoading('');
    }
  };

  if (loading) return <LoadingPanel text="Schreiben werden geladen..." />;

  return (
    <div style={{ display: 'grid', gap: 14 }}>
      {/* Export-Leiste */}
      <div style={panel}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
          <div>
            <h2 style={sectionTitle}>Exporte</h2>
            {!freigegebenesSchreiben && (
              <p style={{ color: '#f87171', fontSize: 12, margin: '4px 0 0' }}>
                Kein freigegebenes Schreiben vorhanden. Bitte zuerst freigeben.
              </p>
            )}
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button
              style={freigegebenesSchreiben ? exportBtn : disabledBtn}
              disabled={!freigegebenesSchreiben || exportLoading === 'pdf'}
              onClick={() => exportieren('pdf')}
              title={!freigegebenesSchreiben ? 'Freigabe erforderlich' : 'Als PDF exportieren'}
            >
              {exportLoading === 'pdf' ? 'Exportiere...' : 'PDF exportieren'}
            </button>
            <button
              style={freigegebenesSchreiben ? exportBtn : disabledBtn}
              disabled={!freigegebenesSchreiben || exportLoading === 'docx'}
              onClick={() => exportieren('docx')}
              title={!freigegebenesSchreiben ? 'Freigabe erforderlich' : 'Als DOCX exportieren'}
            >
              {exportLoading === 'docx' ? 'Exportiere...' : 'DOCX exportieren'}
            </button>
            <button
              style={freigegebenesSchreiben ? exportBtn : disabledBtn}
              disabled={!freigegebenesSchreiben || exportLoading === 'eml'}
              onClick={() => exportieren('eml')}
              title={!freigegebenesSchreiben ? 'Freigabe erforderlich' : 'Als EML exportieren'}
            >
              {exportLoading === 'eml' ? 'Exportiere...' : 'EML exportieren'}
            </button>
          </div>
        </div>
      </div>

      {/* Schreiben-Liste */}
      {schreibenListe.length === 0 ? (
        <div style={emptyState}>Noch kein Schreiben vorhanden.</div>
      ) : (
        schreibenListe.map(s => {
          const istFreigegeben = s.freigegeben === 1 || s.freigegeben === true;
          const istImEdit = editId === s.id;

          return (
            <section key={s.id} style={{ ...panel, borderColor: istFreigegeben ? '#065f4666' : '#1f2937' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <h2 style={{ ...sectionTitle, margin: 0 }}>
                      {istImEdit ? 'Schreiben bearbeiten' : (s.betreff || 'Schreiben ohne Betreff')}
                    </h2>
                    {istFreigegeben && (
                      <span style={{ ...badge, color: '#34d399', borderColor: '#065f46', background: '#064e3b' }}>
                        Freigegeben
                      </span>
                    )}
                    <span style={{ ...badge, color: '#94a3b8', borderColor: '#334155', background: '#1e293b' }}>
                      Version {s.version || 1}
                    </span>
                  </div>
                  <div style={{ color: '#64748b', fontSize: 12, marginTop: 4 }}>
                    Erstellt: {formatDateTime(s.erstellt_am)}
                    {istFreigegeben && s.freigegeben_am && ` · Freigegeben: ${formatDateTime(s.freigegeben_am)}`}
                  </div>
                </div>
                {!istImEdit && (
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    {!istFreigegeben && (
                      <>
                        <button className="btn-secondary" onClick={() => startEdit(s)}>Bearbeiten</button>
                        <button className="btn-secondary" onClick={() => regenerieren(s.id)}>Neu generieren</button>
                        <button className="btn-primary" onClick={() => freigeben(s.id)}>Freigeben</button>
                      </>
                    )}
                    {istFreigegeben && (
                      <span style={{ color: '#34d399', fontSize: 13, fontWeight: 700 }}>
                        Bereit zum Export
                      </span>
                    )}
                  </div>
                )}
              </div>

              {istImEdit ? (
                <div style={{ display: 'grid', gap: 12 }}>
                  <label style={fieldLabel}>
                    <span>Betreff</span>
                    <input
                      value={editBetreff}
                      onChange={e => setEditBetreff(e.target.value)}
                      style={fieldInput}
                      placeholder="Betreff des Kündigungsschreibens"
                    />
                  </label>
                  <label style={fieldLabel}>
                    <span>Schreibentext</span>
                    <textarea
                      value={editText}
                      onChange={e => setEditText(e.target.value)}
                      style={{ ...fieldInput, minHeight: 300, resize: 'vertical', fontFamily: 'monospace', fontSize: 13, lineHeight: 1.6 }}
                      placeholder="Text des Kündigungsschreibens..."
                    />
                  </label>
                  <div style={{ display: 'flex', gap: 10 }}>
                    <button className="btn-primary" onClick={() => speichern(s.id)} disabled={saving}>
                      {saving ? 'Speichern...' : 'Speichern'}
                    </button>
                    <button className="btn-secondary" onClick={abbrechenEdit} disabled={saving}>
                      Abbrechen
                    </button>
                  </div>
                </div>
              ) : (
                <div style={schreibenText}>
                  {s.text ? s.text : <em style={{ color: '#64748b' }}>Kein Inhalt vorhanden.</em>}
                </div>
              )}
            </section>
          );
        })
      )}
    </div>
  );
}

// ─── Tab: Nachweise ────────────────────────────────────────────────────────────

function NachweiseTab({ vorgangId, zeigeMeldung }) {
  const [nachweise, setNachweise] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadTyp, setUploadTyp] = useState('versandnachweis');
  const [uploadBeschreibung, setUploadBeschreibung] = useState('');
  const fileRef = useRef(null);

  const ladeNachweise = useCallback(async () => {
    setLoading(true);
    try {
      const res = await kuendigungenApi.vorgaenge.getNachweise(vorgangId);
      setNachweise(res.data.nachweise || []);
    } catch {
      zeigeMeldung('Nachweise konnten nicht geladen werden.', 'error');
    } finally {
      setLoading(false);
    }
  }, [vorgangId, zeigeMeldung]);

  useEffect(() => { ladeNachweise(); }, [ladeNachweise]);

  const onUpload = async (e) => {
    e.preventDefault();
    const file = fileRef.current?.files?.[0];
    if (!file) {
      zeigeMeldung('Bitte eine Datei auswählen.', 'error');
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('datei', file);
      formData.append('typ', uploadTyp);
      if (uploadBeschreibung.trim()) formData.append('beschreibung', uploadBeschreibung.trim());
      await kuendigungenApi.vorgaenge.addNachweis(vorgangId, formData);
      zeigeMeldung('Nachweis hochgeladen.');
      setUploadBeschreibung('');
      if (fileRef.current) fileRef.current.value = '';
      ladeNachweise();
    } catch (e) {
      zeigeMeldung(e.response?.data?.error || 'Fehler beim Upload.', 'error');
    } finally {
      setUploading(false);
    }
  };

  const onDelete = async (nid) => {
    if (!window.confirm('Nachweis wirklich löschen?')) return;
    try {
      await kuendigungenApi.nachweise.delete(nid, vorgangId);
      zeigeMeldung('Nachweis gelöscht.');
      ladeNachweise();
    } catch (e) {
      zeigeMeldung(e.response?.data?.error || 'Fehler beim Löschen.', 'error');
    }
  };

  return (
    <div style={{ display: 'grid', gap: 14 }}>
      {/* Upload-Formular */}
      <section style={panel}>
        <h2 style={sectionTitle}>Nachweis hochladen</h2>
        <form onSubmit={onUpload} style={{ display: 'grid', gap: 12 }}>
          <label style={fieldLabel}>
            <span>Datei</span>
            <input ref={fileRef} type="file" style={fieldInput} />
          </label>
          <label style={fieldLabel}>
            <span>Typ</span>
            <select value={uploadTyp} onChange={e => setUploadTyp(e.target.value)} style={fieldInput}>
              {NACHWEIS_TYPEN.map(t => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
          </label>
          <label style={fieldLabel}>
            <span>Beschreibung (optional)</span>
            <input
              value={uploadBeschreibung}
              onChange={e => setUploadBeschreibung(e.target.value)}
              placeholder="z.B. Einschreiben-Beleg vom Postamt"
              style={fieldInput}
            />
          </label>
          <button type="submit" className="btn-primary" disabled={uploading}>
            {uploading ? 'Wird hochgeladen...' : 'Hochladen'}
          </button>
        </form>
      </section>

      {/* Nachweise-Liste */}
      <section style={panel}>
        <h2 style={sectionTitle}>Vorhandene Nachweise ({nachweise.length})</h2>
        {loading ? (
          <div style={emptyState}>Wird geladen...</div>
        ) : nachweise.length === 0 ? (
          <div style={emptyState}>Noch keine Nachweise vorhanden.</div>
        ) : (
          <div style={{ display: 'grid', gap: 8 }}>
            {nachweise.map(n => {
              const typLabel = NACHWEIS_TYPEN.find(t => t.value === n.typ)?.label || n.typ || '–';
              return (
                <div key={n.id} style={listCard}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ color: '#f8fafc', fontWeight: 700, fontSize: 14, wordBreak: 'break-all' }}>
                      {n.originaldateiname || n.dateiname}
                    </div>
                    <div style={{ color: '#94a3b8', fontSize: 12, marginTop: 3 }}>
                      {typLabel} · {formatGroesse(n.groesse)} · {formatDate(n.hochgeladen_am)}
                    </div>
                    {n.beschreibung && (
                      <div style={{ color: '#64748b', fontSize: 12, marginTop: 2 }}>{n.beschreibung}</div>
                    )}
                  </div>
                  <button className="btn-danger" onClick={() => onDelete(n.id)}>Löschen</button>
                </div>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}

// ─── Tab: Kommunikation ────────────────────────────────────────────────────────

function KommunikationTab({ vorgangId, zeigeMeldung }) {
  const [kommunikation, setKommunikation] = useState([]);
  const [wiedervorlagen, setWiedervorlagen] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Neuer Kommunikationseintrag
  const [neuTyp, setNeuTyp] = useState('antwort');
  const [neuBetreff, setNeuBetreff] = useState('');
  const [neuInhalt, setNeuInhalt] = useState('');
  const [neuAbsender, setNeuAbsender] = useState('');
  const [neuEingangAm, setNeuEingangAm] = useState('');

  // Neue Wiedervorlage
  const [wvFaelligAm, setWvFaelligAm] = useState('');
  const [wvBetreff, setWvBetreff] = useState('');
  const [wvNotiz, setWvNotiz] = useState('');
  const [wvSaving, setWvSaving] = useState(false);

  const ladeAlles = useCallback(async () => {
    setLoading(true);
    try {
      const [komRes, wvRes] = await Promise.all([
        kuendigungenApi.vorgaenge.getKommunikation(vorgangId),
        kuendigungenApi.vorgaenge.getWiedervorlagen(vorgangId),
      ]);
      setKommunikation(komRes.data.kommunikation || []);
      setWiedervorlagen(wvRes.data.wiedervorlagen || []);
    } catch {
      zeigeMeldung('Kommunikation konnte nicht geladen werden.', 'error');
    } finally {
      setLoading(false);
    }
  }, [vorgangId, zeigeMeldung]);

  useEffect(() => { ladeAlles(); }, [ladeAlles]);

  const onAddKommunikation = async (e) => {
    e.preventDefault();
    if (!neuBetreff.trim() || !neuInhalt.trim()) {
      zeigeMeldung('Bitte Betreff und Inhalt ausfüllen.', 'error');
      return;
    }
    setSaving(true);
    try {
      const data = {
        typ: neuTyp,
        betreff: neuBetreff.trim(),
        inhalt: neuInhalt.trim(),
        absender: neuAbsender.trim() || undefined,
        eingang_am: neuEingangAm || undefined,
      };
      await kuendigungenApi.vorgaenge.addKommunikation(vorgangId, data);
      zeigeMeldung('Eintrag gespeichert.');
      setNeuBetreff('');
      setNeuInhalt('');
      setNeuAbsender('');
      setNeuEingangAm('');
      ladeAlles();
    } catch (e) {
      zeigeMeldung(e.response?.data?.error || 'Fehler beim Speichern.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const onKomStatusChange = async (kid, status) => {
    try {
      await kuendigungenApi.kommunikation.updateStatus(kid, status);
      zeigeMeldung(`Status aktualisiert: ${KOMMUNIKATION_STATUS_LABELS[status] || status}`);
      ladeAlles();
    } catch (e) {
      zeigeMeldung(e.response?.data?.error || 'Fehler beim Statuswechsel.', 'error');
    }
  };

  const onAddWiedervorlage = async (e) => {
    e.preventDefault();
    if (!wvFaelligAm || !wvBetreff.trim()) {
      zeigeMeldung('Bitte Fälligkeitsdatum und Betreff ausfüllen.', 'error');
      return;
    }
    setWvSaving(true);
    try {
      await kuendigungenApi.vorgaenge.addWiedervorlage(vorgangId, {
        faellig_am: wvFaelligAm,
        titel: wvBetreff.trim(),
        beschreibung: wvNotiz.trim() || undefined,
      });
      zeigeMeldung('Wiedervorlage erstellt.');
      setWvFaelligAm('');
      setWvBetreff('');
      setWvNotiz('');
      ladeAlles();
    } catch (e) {
      zeigeMeldung(e.response?.data?.error || 'Fehler beim Erstellen.', 'error');
    } finally {
      setWvSaving(false);
    }
  };

  const onWvErledigt = async (wid, erledigt) => {
    try {
      await kuendigungenApi.wiedervorlagen.update(wid, { status: erledigt ? 'erledigt' : 'offen' });
      zeigeMeldung(erledigt ? 'Als erledigt markiert.' : 'Wiedervorlage wieder geöffnet.');
      ladeAlles();
    } catch (e) {
      zeigeMeldung(e.response?.data?.error || 'Fehler.', 'error');
    }
  };

  const onWvDelete = async (wid) => {
    if (!window.confirm('Wiedervorlage wirklich löschen?')) return;
    try {
      await kuendigungenApi.wiedervorlagen.delete(wid);
      zeigeMeldung('Wiedervorlage gelöscht.');
      ladeAlles();
    } catch (e) {
      zeigeMeldung(e.response?.data?.error || 'Fehler beim Löschen.', 'error');
    }
  };

  if (loading) return <LoadingPanel text="Kommunikation wird geladen..." />;

  return (
    <div style={{ display: 'grid', gap: 14 }}>
      {/* Kommunikation: Eingehende Antworten */}
      <section style={panel}>
        <h2 style={sectionTitle}>Eingegangene Antworten & Einträge ({kommunikation.length})</h2>
        {kommunikation.length === 0 ? (
          <div style={emptyState}>Noch keine Einträge vorhanden.</div>
        ) : (
          <div style={{ display: 'grid', gap: 10 }}>
            {kommunikation.map(k => {
              const statusFarbe = k.status === 'erledigt' ? '#34d399' : k.status === 'abgelehnt' ? '#f87171' : k.status === 'in_bearbeitung' ? '#fbbf24' : '#94a3b8';
              return (
                <div key={k.id} style={{ ...listCard, flexDirection: 'column', gap: 8 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10, flexWrap: 'wrap' }}>
                    <div>
                      <div style={{ color: '#f8fafc', fontWeight: 700, fontSize: 14 }}>{k.betreff || '(kein Betreff)'}</div>
                      <div style={{ color: '#94a3b8', fontSize: 12, marginTop: 2 }}>
                        {k.typ && <span style={{ marginRight: 8 }}>{k.typ}</span>}
                        {k.absender && <span style={{ marginRight: 8 }}>Von: {k.absender}</span>}
                        {k.eingang_am && <span>Eingang: {formatDate(k.eingang_am)}</span>}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexShrink: 0 }}>
                      <span style={{ ...badge, color: statusFarbe, borderColor: `${statusFarbe}66`, background: `${statusFarbe}14` }}>
                        {KOMMUNIKATION_STATUS_LABELS[k.status] || k.status || 'Offen'}
                      </span>
                      <select
                        value={k.status || 'offen'}
                        onChange={e => onKomStatusChange(k.id, e.target.value)}
                        style={{ ...fieldInput, padding: '4px 7px', fontSize: 12 }}
                      >
                        <option value="offen">Offen</option>
                        <option value="in_bearbeitung">In Bearbeitung</option>
                        <option value="erledigt">Erledigt</option>
                        <option value="abgelehnt">Abgelehnt</option>
                      </select>
                    </div>
                  </div>
                  {k.inhalt && (
                    <div style={{ color: '#cbd5e1', fontSize: 13, whiteSpace: 'pre-wrap', background: '#0f172a', borderRadius: 6, padding: '8px 10px' }}>
                      {k.inhalt}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* Kommunikation: Neuen Eintrag hinzufügen */}
      <section style={panel}>
        <h2 style={sectionTitle}>Neuen Eintrag hinzufügen</h2>
        <form onSubmit={onAddKommunikation} style={{ display: 'grid', gap: 12 }}>
          <div style={twoColForm}>
            <label style={fieldLabel}>
              <span>Typ</span>
              <select value={neuTyp} onChange={e => setNeuTyp(e.target.value)} style={fieldInput}>
                <option value="antwort">Antwort</option>
                <option value="rueckfrage">Rückfrage</option>
                <option value="ablehnung">Ablehnung</option>
                <option value="bestaetigung">Bestätigung</option>
                <option value="sonstiges">Sonstiges</option>
              </select>
            </label>
            <label style={fieldLabel}>
              <span>Eingang am</span>
              <input
                type="date"
                value={neuEingangAm}
                onChange={e => setNeuEingangAm(e.target.value)}
                style={fieldInput}
              />
            </label>
          </div>
          <label style={fieldLabel}>
            <span>Absender</span>
            <input
              value={neuAbsender}
              onChange={e => setNeuAbsender(e.target.value)}
              placeholder="z.B. Kundenservice Anbieter GmbH"
              style={fieldInput}
            />
          </label>
          <label style={fieldLabel}>
            <span>Betreff *</span>
            <input
              value={neuBetreff}
              onChange={e => setNeuBetreff(e.target.value)}
              placeholder="Betreff des Eintrags"
              style={fieldInput}
              required
            />
          </label>
          <label style={fieldLabel}>
            <span>Inhalt *</span>
            <textarea
              value={neuInhalt}
              onChange={e => setNeuInhalt(e.target.value)}
              placeholder="Inhalt der Antwort oder des Eintrags..."
              style={{ ...fieldInput, minHeight: 120, resize: 'vertical' }}
              required
            />
          </label>
          <button type="submit" className="btn-primary" disabled={saving}>
            {saving ? 'Speichern...' : 'Eintrag speichern'}
          </button>
        </form>
      </section>

      {/* Wiedervorlagen */}
      <section style={panel}>
        <h2 style={sectionTitle}>Wiedervorlagen ({wiedervorlagen.length})</h2>
        {wiedervorlagen.length === 0 ? (
          <div style={emptyState}>Keine Wiedervorlagen vorhanden.</div>
        ) : (
          <div style={{ display: 'grid', gap: 8, marginBottom: 14 }}>
            {wiedervorlagen.map(w => {
              const istErledigt = w.status === 'erledigt';
              const faelligDate = w.faellig_am ? new Date(String(w.faellig_am).replace(' ', 'T')) : null;
              const istUeberfaellig = faelligDate && faelligDate < new Date() && !istErledigt;
              return (
                <div key={w.id} style={{ ...listCard, opacity: istErledigt ? 0.6 : 1, borderColor: istUeberfaellig ? '#f8717166' : istErledigt ? '#065f4666' : '#334155' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      <span style={{ color: '#f8fafc', fontWeight: 700, fontSize: 14 }}>{w.titel}</span>
                      {istUeberfaellig && (
                        <span style={{ ...badge, color: '#f87171', borderColor: '#f8717166', background: '#f8717114' }}>Überfällig</span>
                      )}
                      {istErledigt && (
                        <span style={{ ...badge, color: '#34d399', borderColor: '#065f46', background: '#064e3b' }}>Erledigt</span>
                      )}
                    </div>
                    <div style={{ color: '#94a3b8', fontSize: 12, marginTop: 3 }}>
                      Fällig: {formatDate(w.faellig_am)}
                    </div>
                    {w.beschreibung && (
                      <div style={{ color: '#64748b', fontSize: 12, marginTop: 3 }}>{w.beschreibung}</div>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                    {!istErledigt ? (
                      <button style={confirmBtn} onClick={() => onWvErledigt(w.id, true)}>Erledigt</button>
                    ) : (
                      <button className="btn-secondary" onClick={() => onWvErledigt(w.id, false)}>Wieder öffnen</button>
                    )}
                    <button className="btn-danger" onClick={() => onWvDelete(w.id)}>Löschen</button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* Neue Wiedervorlage */}
      <section style={panel}>
        <h2 style={sectionTitle}>Neue Wiedervorlage</h2>
        <form onSubmit={onAddWiedervorlage} style={{ display: 'grid', gap: 12 }}>
          <div style={twoColForm}>
            <label style={fieldLabel}>
              <span>Fällig am *</span>
              <input
                type="date"
                value={wvFaelligAm}
                onChange={e => setWvFaelligAm(e.target.value)}
                style={fieldInput}
                required
              />
            </label>
            <label style={fieldLabel}>
              <span>Betreff *</span>
              <input
                value={wvBetreff}
                onChange={e => setWvBetreff(e.target.value)}
                placeholder="z.B. Antwort prüfen"
                style={fieldInput}
                required
              />
            </label>
          </div>
          <label style={fieldLabel}>
            <span>Notiz (optional)</span>
            <input
              value={wvNotiz}
              onChange={e => setWvNotiz(e.target.value)}
              placeholder="Weitere Hinweise..."
              style={fieldInput}
            />
          </label>
          <button type="submit" className="btn-primary" disabled={wvSaving}>
            {wvSaving ? 'Erstellen...' : 'Wiedervorlage erstellen'}
          </button>
        </form>
      </section>
    </div>
  );
}

// ─── Tab: Details ──────────────────────────────────────────────────────────────

function DetailsTab({ vorgang, onStatusChange }) {
  const [neuerStatus, setNeuerStatus] = useState('');

  const erlaubteStatus = ERLAUBTE_FOLGERSTATUS[vorgang.status] || [];

  const onSubmitStatus = (e) => {
    e.preventDefault();
    if (!neuerStatus) return;
    onStatusChange(neuerStatus);
    setNeuerStatus('');
  };

  return (
    <div style={{ display: 'grid', gap: 14 }}>
      {/* Statuswechsel */}
      <section style={panel}>
        <h2 style={sectionTitle}>Statuswechsel</h2>
        <div style={{ marginBottom: 12 }}>
          <span style={{ color: '#94a3b8', fontSize: 13 }}>Aktueller Status: </span>
          <StatusBadge status={vorgang.status} />
        </div>
        {erlaubteStatus.length === 0 ? (
          <div style={{ color: '#64748b', fontSize: 13 }}>
            Für diesen Status sind keine weiteren Übergänge möglich.
          </div>
        ) : (
          <form onSubmit={onSubmitStatus} style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <label style={{ ...fieldLabel, flex: 1, minWidth: 200 }}>
              <span>Neuer Status</span>
              <select
                value={neuerStatus}
                onChange={e => setNeuerStatus(e.target.value)}
                style={fieldInput}
              >
                <option value="">– Status wählen –</option>
                {erlaubteStatus.map(s => (
                  <option key={s} value={s}>{STATUS_LABELS[s] || s}</option>
                ))}
              </select>
            </label>
            <button type="submit" className="btn-primary" disabled={!neuerStatus}>
              Status setzen
            </button>
          </form>
        )}
      </section>

      {/* Metadaten */}
      <section style={panel}>
        <h2 style={sectionTitle}>Vorgangsdaten</h2>
        <div style={infoGrid}>
          <InfoBox label="Vertrag" value={vorgang.vertrag_titel} />
          <InfoBox label="Anbieter" value={vorgang.vertrag_anbieter} />
          <InfoBox label="Status" value={<StatusBadge status={vorgang.status} />} />
          <InfoBox label="Kündigungsdatum" value={formatDate(vorgang.kuendigungsdatum)} />
          <InfoBox label="Fristgerecht" value={vorgang.fristgerecht === 1 ? 'Ja' : vorgang.fristgerecht === 0 ? 'Nein' : '–'} />
          <InfoBox label="Sonderkündigung" value={vorgang.sonderkuendigung === 1 ? 'Ja' : 'Nein'} />
          <InfoBox label="Versanddatum" value={formatDate(vorgang.versanddatum)} />
          <InfoBox label="Versandart" value={vorgang.versandart} />
          <InfoBox label="Tracking-Nummer" value={vorgang.tracking_nummer} />
          <InfoBox label="Bestätigt am" value={formatDate(vorgang.bestaetigt_am)} />
          <InfoBox label="Erstellt am" value={formatDateTime(vorgang.erstellt_am)} />
          <InfoBox label="Aktualisiert am" value={formatDateTime(vorgang.aktualisiert_am)} />
        </div>

        {vorgang.kuendigungsgrund && (
          <div style={{ marginTop: 14 }}>
            <div style={infoLabel}>Kündigungsgrund</div>
            <div style={{ color: '#cbd5e1', fontSize: 13, marginTop: 4, whiteSpace: 'pre-wrap' }}>
              {vorgang.kuendigungsgrund}
            </div>
          </div>
        )}

        {vorgang.sonderkuendigung === 1 && vorgang.sonderkuendigungsgrund && (
          <div style={{ marginTop: 14, background: '#3b2009', border: '1px solid #fbbf2444', borderRadius: 7, padding: '10px 12px' }}>
            <div style={{ color: '#fbbf24', fontSize: 11, fontWeight: 800, textTransform: 'uppercase', marginBottom: 4 }}>
              Sonderkündigungsgrund (Hinweis)
            </div>
            <div style={{ color: '#fde68a', fontSize: 13 }}>{vorgang.sonderkuendigungsgrund}</div>
            <div style={{ color: '#b45309', fontSize: 12, marginTop: 6 }}>
              Hinweis: Sonderkündigungen werden nicht automatisch ausgelöst. Bitte prüfen Sie den Sachverhalt und handeln Sie manuell.
            </div>
          </div>
        )}

        {vorgang.notizen && (
          <div style={{ marginTop: 14 }}>
            <div style={infoLabel}>Notizen</div>
            <div style={{ color: '#cbd5e1', fontSize: 13, marginTop: 4, whiteSpace: 'pre-wrap' }}>
              {vorgang.notizen}
            </div>
          </div>
        )}
      </section>
    </div>
  );
}

// ─── Hilfskompomenten ──────────────────────────────────────────────────────────

function InfoBox({ label, value }) {
  return (
    <div style={{ display: 'grid', gap: 2 }}>
      <span style={infoLabel}>{label}</span>
      <span style={{ color: '#e5e7eb', fontSize: 13 }}>{value || '–'}</span>
    </div>
  );
}

function Screen({ title = 'Bitte warten', text }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', textAlign: 'center' }}>
      <h2 style={{ color: '#f8fafc', fontSize: 22 }}>{title}</h2>
      <p style={{ color: '#94a3b8' }}>{text}</p>
    </div>
  );
}

function LoadingPanel({ text }) {
  return (
    <section style={panel}>
      <div style={{ color: '#94a3b8', fontSize: 13 }}>{text}</div>
    </section>
  );
}

// ─── Styles ────────────────────────────────────────────────────────────────────

const page = { padding: 18, maxWidth: 1100, margin: '0 auto', display: 'grid', gap: 16 };
const header = { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 14, background: '#111827', border: '1px solid #1f2937', borderRadius: 8, padding: 20, flexWrap: 'wrap' };
const eyebrow = { color: '#38bdf8', fontSize: 12, fontWeight: 800, textTransform: 'uppercase' };
const titleStyle = { color: '#f8fafc', fontSize: 26, margin: '4px 0 8px', lineHeight: 1.2, overflowWrap: 'anywhere' };
const subtitleRow = { display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' };
const typChip = { color: '#38bdf8', border: '1px solid #155e75', background: '#083344', borderRadius: 999, padding: '4px 10px', fontSize: 11, fontWeight: 800 };
const notice = { color: '#bfdbfe', background: '#172554', border: '1px solid #1d4ed8', borderRadius: 7, padding: '10px 14px', fontSize: 13 };
const errorNotice = { color: '#fecaca', background: '#2b0a0a', border: '1px solid #7f1d1d', borderRadius: 7, padding: '10px 14px', fontSize: 13 };
const kiHinweisBox = { color: '#bfdbfe', background: '#1e1a4e', border: '1px solid #3730a3', borderRadius: 7, padding: '10px 14px', fontSize: 13 };
const sonderHinweisBox = { color: '#fde68a', background: '#3b2009', border: '1px solid #fbbf2444', borderRadius: 7, padding: '10px 14px', fontSize: 13 };
const panel = { background: '#111827', border: '1px solid #1f2937', borderRadius: 8, padding: 16 };
const sectionTitle = { color: '#f8fafc', fontSize: 16, fontWeight: 700, margin: '0 0 12px' };
const tabRow = { display: 'flex', gap: 8, background: '#0f172a', borderRadius: 8, padding: 4, flexWrap: 'wrap' };
const tabBtn = { background: 'transparent', border: 0, color: '#94a3b8', padding: '8px 16px', borderRadius: 6, cursor: 'pointer', fontWeight: 700, fontSize: 13 };
const activeTabBtn = { ...tabBtn, background: '#1f2937', color: '#f8fafc' };
const fieldLabel = { display: 'grid', gap: 5, color: '#94a3b8', fontSize: 12, fontWeight: 700 };
const fieldInput = { background: '#0f172a', border: '1px solid #334155', color: '#f8fafc', borderRadius: 7, padding: '10px 9px', fontSize: 13 };
const exportBtn = { background: '#064e3b', color: '#34d399', border: '1px solid #065f46', borderRadius: 7, padding: '9px 14px', fontWeight: 800, cursor: 'pointer', fontSize: 13 };
const disabledBtn = { background: '#1e293b', color: '#475569', border: '1px solid #334155', borderRadius: 7, padding: '9px 14px', fontWeight: 800, cursor: 'not-allowed', fontSize: 13 };
const confirmBtn = { background: '#064e3b', color: '#34d399', border: '1px solid #065f46', borderRadius: 6, padding: '5px 10px', cursor: 'pointer', fontWeight: 800, fontSize: 13 };
const badge = { border: '1px solid', borderRadius: 999, padding: '3px 8px', fontSize: 11, fontWeight: 800 };
const listCard = { background: '#0f172a', border: '1px solid #334155', borderRadius: 7, padding: '10px 12px', display: 'flex', alignItems: 'center', gap: 12 };
const infoGrid = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12 };
const infoLabel = { color: '#64748b', fontSize: 11, fontWeight: 800, textTransform: 'uppercase' };
const emptyState = { color: '#94a3b8', background: '#0f172a', border: '1px dashed #334155', borderRadius: 8, padding: 14, fontSize: 13 };
const schreibenText = { color: '#cbd5e1', fontSize: 13, whiteSpace: 'pre-wrap', background: '#0f172a', borderRadius: 7, padding: '12px 14px', lineHeight: 1.7, border: '1px solid #1e293b' };
const twoColForm = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 };
